import isHeadless from '../../headless/isHeadless'
import { isMobile } from '../browser'

declare global {
  interface Window {
    LIVE_ID: string
    LIVE_TOKEN: string
  }
}

/*
  The purpose of this function is to extract the global info that should live throughout the entire lifespan of a webinar room
  That information being liveId, presenterToken and role.
 */
const extractRoomGlobalInfo = (url?: string) => {
  const parsedUrl = new URL(url ?? window.location.href)
  const path = parsedUrl.pathname
  const params = new URLSearchParams(parsedUrl.search)
  const isFastForwarding = params.get('ff') === '1'
  const isSecondCam = params.get('is_second_cam') === '1'

  if (isHeadless) {
    const liveId = params.get('live_id')
    const presenterToken = params.get('presenter_token')
    if (!!liveId && !!presenterToken) {
      return {
        isFastForwarding,
        role: 'headless',
        liveId: +liveId,
        presenterToken,
        speakerId: 0,
        isSecondCam,
      } as const
    }
  } else {
    const producerMatch = path.match(/\/producer\/(\d+)/)
    if (producerMatch) {
      return {
        isFastForwarding,
        role: 'producer',
        liveId: +producerMatch[1],
        isSecondCam,
      } as const
    }

    const presenterMatch = path.match(
      /\/presenter\/(\d+)\/([^/]+-[^/]+-([^/]+))/
    )
    if (presenterMatch) {
      return {
        isFastForwarding,
        role: 'presenter',
        liveId: +presenterMatch[1],
        presenterToken: presenterMatch[2],
        speakerId: +presenterMatch[3],
        isSecondCam,
      } as const
    }

    // audience
    return {
      role: 'audience',
      liveId: +window.LIVE_ID,
      audienceToken: window.LIVE_TOKEN,
    } as const
  }

  throw new Error('Could not extract room info')
}

type RoomGlobalInfo = {
  liveId: number
} & (
  | {
      role: 'presenter' | 'headless'
      presenterToken: string
      isFastForwarding: boolean
      isSecondCam: boolean
      speakerId: number
    }
  | {
      role: 'producer'
      isFastForwarding: boolean
      isSecondCam: boolean
    }
  | {
      role: 'audience'
      audienceToken: string
    }
)

const roomGlobalInfo: RoomGlobalInfo = Object.freeze(extractRoomGlobalInfo())

// TODO: remove and use the gets
const useRoomGlobalInfo = () => roomGlobalInfo
const useLiveId = () => roomGlobalInfo.liveId
const useRole = () => roomGlobalInfo.role

const getRoomInfo = () => roomGlobalInfo
const getLiveId = () => roomGlobalInfo.liveId
const getAudienceToken = () =>
  roomGlobalInfo.role === 'audience' ? roomGlobalInfo.audienceToken : undefined

const getRole = () => roomGlobalInfo.role
const getPresenterToken = () =>
  roomGlobalInfo.role === 'presenter' || roomGlobalInfo.role === 'headless'
    ? roomGlobalInfo.presenterToken
    : undefined

const getIsClientFastForwarding = () =>
  roomGlobalInfo.role !== 'audience' && roomGlobalInfo.isFastForwarding
const getIsClientSecondCam = () =>
  roomGlobalInfo.role !== 'audience' && roomGlobalInfo.isSecondCam && isMobile()

export {
  type RoomGlobalInfo,
  useRoomGlobalInfo,
  useLiveId,
  useRole,
  getRoomInfo,
  getLiveId,
  getAudienceToken,
  getRole,
  getPresenterToken,
  getIsClientFastForwarding,
  getIsClientSecondCam,
}
